import { motion } from 'framer-motion';
import { FiCheckCircle, FiLayers, FiServer } from 'react-icons/fi';
import SectionHeading from './SectionHeading';
import Icon from './Icon';
import { SiteConfig } from '../types/portfolio';

type AboutProps = {
  siteConfig: SiteConfig;
};

function About({ siteConfig }: AboutProps) {
  return (
    <section id="about" className="section-shell">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading
          eyebrow="About"
          title="Enterprise software engineering with backend depth and frontend clarity."
          description="Built for large-scale delivery, this portfolio highlights a senior engineer focused on scalable Java systems, clean APIs, and modern React-based user experiences."
        />

        <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <motion.article
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5 }}
            className="glass-panel rounded-[2rem] p-8"
          >
            <div className="flex items-center gap-3 text-sky-200">
              <Icon icon={FiServer} />
              <span className="text-sm font-semibold uppercase tracking-[0.3em]">Professional Introduction</span>
            </div>
            <div className="mt-6 space-y-5 text-base leading-8 text-slate-300">
              {siteConfig.summary.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </motion.article>

          <motion.aside
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="glass-panel rounded-[2rem] p-8"
          >
            <div className="flex items-center gap-3 text-teal-200">
              <Icon icon={FiLayers} />
              <span className="text-sm font-semibold uppercase tracking-[0.3em]">Core Strengths</span>
            </div>
            <div className="mt-6 grid gap-3">
              {siteConfig.aboutHighlights.map((highlight) => (
                <div
                  key={highlight}
                  className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-4"
                >
                  <Icon icon={FiCheckCircle} className="mt-1 text-teal-300" />
                  <span className="text-sm leading-6 text-slate-200">{highlight}</span>
                </div>
              ))}
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}

export default About;
